import type {
  Reminder,
  ReminderCreate,
  ReminderData,
  ReminderUpdate,
} from './types';
import { Paths } from '@/store/constants';
import { apiInvoke } from '@/store/helpers/load';
import { makePayloadRequestInit } from '@/lib/api';
import type { APIParams } from '@/store/helpers/types';
import type { UserId } from '@/shared/types/user';

export async function loadReminders({
  api,
  auth,
  assignee,
  limit,
}: { assignee: UserId; limit?: number } & APIParams) {
  const params = new URLSearchParams({ assignee });
  if (limit) {
    params.append('limit', limit.toString());
  }

  const path = `${Paths.reminders}?${params.toString()}`;
  const response = await apiInvoke<LoadRemindersResponse>(api, path, auth, {
    method: 'GET',
  });

  const reminders: Reminder[] = response.data.map(
    ({ id, fullname, firstname, lastname, ...rest }) => ({
      id,
      data: rest,
      name: {
        fullname,
        firstname,
        lastname,
      },
    }),
  );

  return reminders;
}

type LoadRemindersResponse = {
  data: Array<
    ReminderData & {
      id: string;
      fullname?: string;
      firstname?: string;
      lastname?: string;
    }
  >;
};

type CreateReminderResponse = Reminder;

export async function createReminder({
  api,
  auth,
  reminder,
}: { reminder: ReminderCreate } & APIParams) {
  const path = `${Paths.reminders}/create`;
  const payload = reminder.data;
  const response = await apiInvoke<CreateReminderResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return response;
}

type UpdateReminderResponse = Reminder;

export async function updateReminder({
  api,
  auth,
  reminder,
}: { reminder: ReminderUpdate } & APIParams) {
  const path = `${Paths.reminders}/update`;
  const payload = { id: reminder.id, ...reminder.data };
  const response = await apiInvoke<UpdateReminderResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return response;
}

type DismissReminderResponse = Reminder;

export async function dismissReminder({
  api,
  auth,
  id,
}: { id: string } & APIParams) {
  const path = `${Paths.reminders}/dismiss`;
  const payload = {
    id,
  };
  const response = await apiInvoke<DismissReminderResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return response;
}
